import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { LogoMark } from "./Logo";

const anchors = [
  { hash: "produkt", label: "Produkt" },
  { hash: "datenschutz", label: "Datenschutz" },
  { hash: "musteranalyse", label: "Musteranalyse" },
  { hash: "zielgruppen", label: "Zielgruppen" },
  { hash: "faq", label: "FAQ" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Build hrefs: on landing use "#hash", elsewhere use "/#hash".
  const anchorHref = (hash) => (onHome ? `#${hash}` : `/#${hash}`);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0F172A]/70 border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <LogoMark size={36} />
          <span className="font-display text-lg font-semibold tracking-tight">
            Be<span className="text-[#00FAF8]">Parent</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {anchors.map((l) => (
            <a
              key={l.hash}
              data-testid={`nav-link-${l.hash}`}
              href={anchorHref(l.hash)}
              className="bp-link text-sm text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/ratgeber"
            data-testid="nav-link-ratgeber"
            className="bp-link text-sm text-white/70 hover:text-white transition-colors"
          >
            Ratgeber
          </Link>
          <Link
            to="/kontakt"
            data-testid="nav-link-kontakt"
            className="bp-link text-sm text-white/70 hover:text-white transition-colors"
          >
            Kontakt
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Menü öffnen"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 text-white/80"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-[#0F172A]/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {anchors.map((l) => (
                <a
                  key={l.hash}
                  href={anchorHref(l.hash)}
                  onClick={() => setOpen(false)}
                  className="py-3 text-white/80 hover:text-[#00FAF8] transition-colors border-b border-white/5"
                  data-testid={`nav-mobile-link-${l.hash}`}
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/ratgeber"
                onClick={() => setOpen(false)}
                className="py-3 text-white/80 hover:text-[#00FAF8] transition-colors border-b border-white/5"
                data-testid="nav-mobile-link-ratgeber"
              >
                Ratgeber
              </Link>
              <Link
                to="/kontakt"
                onClick={() => setOpen(false)}
                className="py-3 text-white/80 hover:text-[#00FAF8] transition-colors border-b border-white/5"
                data-testid="nav-mobile-link-kontakt"
              >
                Kontakt
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
